import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { trpc } from "../../app/trpc.js";
import {
  Badge,
  Button,
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Chip,
  EmptyState,
  focusRing,
  IconButton,
  Notice,
  PageTitle,
  PlusIcon,
  SkeletonList,
  Spinner,
  useWide,
} from "../../ui/index.js";
import { addDays, dayKey, dayStart, fmtDay, fmtTime, useCabinet, weekStart } from "../cabinet/index.js";
import { BookingSheet } from "./BookingSheet.js";
import { NewBookingSheet } from "./NewBookingSheet.js";
import { isDead, statusTone } from "./status.js";
import { TimeOffSheet } from "./TimeOffSheet.js";
import { type GridBooking, type GridTimeOff, masterColor, WeekGrid } from "./WeekGrid.js";

/**
 * Календарь. На телефоне неделя полосой, день списком; на широком экране неделя сеткой по всем мастерам
 * (`WeekGrid`), детали записи в боковой панели. Владелец видит всех мастеров (фильтр чипами),
 * мастер только себя (ограничивает сервер). `?booking=<id>` открывает запись (переход из уведомления).
 */
/** Ждём пояс салона: «сегодня» считается в нём и запоминается в состоянии. */
export function CalendarPage() {
  const cab = useCabinet();
  if (cab.loading) return <Spinner />;
  return <CalendarPageView />;
}

function CalendarPageView() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const cab = useCabinet();
  const tz = cab.timezone;
  const [params, setParams] = useSearchParams();
  const today = dayKey(new Date(), tz);
  const [day, setDay] = useState(params.get("day") ?? today);
  const [master, setMaster] = useState<string>("all");
  const [newOpen, setNewOpen] = useState(false);
  const [offOpen, setOffOpen] = useState(false);
  const openBookingId = params.get("booking");
  const wide = useWide();

  const week = weekStart(day);
  const range = useMemo(() => ({ from: dayStart(week, tz), to: dayStart(addDays(week, 7), tz) }), [week, tz]);
  const masters = useQuery(trpc.calendar.masters.list.queryOptions({ salonId: cab.salonId }));
  const agenda = useQuery(
    trpc.cabinet.agenda.queryOptions(
      { salonId: cab.salonId, ...range, ...(cab.isOwner && master !== "all" ? { masterId: master } : {}) },
      { refetchInterval: 30_000, enabled: !cab.loading },
    ),
  );

  const masterName = (id: string) => masters.data?.find((m) => m.id === id)?.name ?? "";
  const bookings = agenda.data?.bookings ?? [];
  const countByDay = new Map<string, number>();
  for (const b of bookings) if (!isDead(b.status)) countByDay.set(dayKey(b.period.start, tz), (countByDay.get(dayKey(b.period.start, tz)) ?? 0) + 1);

  type Row = { kind: "booking"; b: (typeof bookings)[number] } | { kind: "off"; o: NonNullable<typeof agenda.data>["timeOff"][number] };
  const rows: Row[] = [
    ...bookings.filter((b) => dayKey(b.period.start, tz) === day).map((b) => ({ kind: "booking" as const, b })),
    ...(agenda.data?.timeOff ?? [])
      .filter((o) => o.period.start < dayStart(addDays(day, 1), tz) && o.period.end > dayStart(day, tz))
      .map((o) => ({ kind: "off" as const, o })),
  ].sort(
    (a, b) => (a.kind === "booking" ? a.b.period.start : a.o.period.start).getTime() - (b.kind === "booking" ? b.b.period.start : b.o.period.start).getTime(),
  );

  const selectDay = (k: string) => {
    setDay(k);
    params.delete("day");
    setParams(params, { replace: true });
  };
  // Переход с одной открытой записи на другую не плодит записи в истории: «Назад» закрывает панель, а не листает записи.
  const openBooking = (id: string | null) => {
    if (id) params.set("booking", id);
    else params.delete("booking");
    setParams(params, { replace: !id || Boolean(openBookingId) });
  };
  // Шторка новой записи или отгула поверх открытой боковой панели записи: панель закрываем, иначе Esc закрыл бы обе,
  // а панель оставалась бы кликабельной поверх затемнения.
  const openModal = (set: (v: boolean) => void) => {
    if (openBookingId) openBooking(null);
    set(true);
  };
  const showMasterName = cab.isOwner && master === "all" && (masters.data?.length ?? 0) > 1;
  const masterIndex = (id: string) => masters.data?.findIndex((m) => m.id === id) ?? -1;
  const clientLabel = (b: (typeof bookings)[number]) =>
    (b.clientId && agenda.data?.clients[b.clientId]?.name) || (b.clientId && agenda.data?.clients[b.clientId]?.instagram) || t("cal.noClient");
  const detailsLabel = (b: (typeof bookings)[number]) =>
    [b.items.map((i) => i.name).join(" + "), showMasterName ? masterName(b.masterId) : "", b.source === "bot" ? t("cal.byBot") : ""]
      .filter(Boolean)
      .join(" · ");
  const canCreate = cab.isOwner || Boolean(cab.masterId);
  const gridBookings: GridBooking[] = bookings.map((b) => ({
    id: b.id,
    start: b.period.start,
    end: b.period.end,
    status: b.status,
    masterIndex: masterIndex(b.masterId),
    client: clientLabel(b),
    details: detailsLabel(b),
  }));
  const gridTimeOff: GridTimeOff[] = (agenda.data?.timeOff ?? []).map((o) => ({
    id: o.id,
    start: o.period.start,
    end: o.period.end,
    masterIndex: masterIndex(o.masterId),
    label: [t(`cal.offKind.${o.kind}`), showMasterName ? masterName(o.masterId) : ""].filter(Boolean).join(" · "),
  }));
  // При фильтре «Все» отгул одного мастера занимает свою дорожку в колонке дня, а не всю колонку.
  const masterCount = showMasterName ? (masters.data?.length ?? 1) : 1;

  if (cab.loading) return <Spinner />;
  return (
    <div>
      <PageTitle
        actions={
          <>
            <Button variant="ghost" size="sm" onClick={() => openModal(setOffOpen)}>
              {t("cal.timeOff")}
            </Button>
            {day !== today && (
              <Button variant="secondary" size="sm" onClick={() => selectDay(today)}>
                {t("cal.today")}
              </Button>
            )}
            {wide && (
              <Button size="sm" icon={<PlusIcon />} disabled={!canCreate} onClick={() => openModal(setNewOpen)}>
                {t("cal.new")}
              </Button>
            )}
          </>
        }
      >
        {t("cal.title")}
      </PageTitle>

      {wide && (
        <div className="mb-4 flex items-center gap-2">
          <IconButton size="sm" label={t("cal.prevWeek")} icon={<ChevronLeftIcon />} onClick={() => selectDay(addDays(day, -7))} />
          <div className="text-sm font-medium text-ink">
            {fmtDay(week, lang, { day: "numeric", month: "long" })} – {fmtDay(addDays(week, 6), lang, { day: "numeric", month: "long", year: "numeric" })}
          </div>
          <IconButton size="sm" label={t("cal.nextWeek")} icon={<ChevronRightIcon />} onClick={() => selectDay(addDays(day, 7))} />
        </div>
      )}

      {!wide && (
        <div className="-mx-1 mb-4 flex items-center gap-0.5">
          <IconButton size="sm" label={t("cal.prevWeek")} icon={<ChevronLeftIcon />} onClick={() => selectDay(addDays(day, -7))} />
          <div className="grid flex-1 grid-cols-7 gap-1">
            {Array.from({ length: 7 }, (_, i) => addDays(week, i)).map((k) => {
              const n = countByDay.get(k) ?? 0;
              const active = k === day;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => selectDay(k)}
                  aria-pressed={active}
                  aria-current={k === today ? "date" : undefined}
                  className={`flex flex-col items-center rounded-field py-1.5 text-xs transition ${focusRing} ${
                    active ? "bg-brand text-white" : k === today ? "bg-brand-soft text-brand-text" : "text-ink-3 hover:bg-slate-100"
                  }`}
                >
                  <span className={active ? "opacity-80" : ""}>{fmtDay(k, lang, { weekday: "short" })}</span>
                  <span className={`text-base font-semibold ${active ? "" : "text-ink"}`}>{Number(k.slice(8))}</span>
                  <span className={`h-1.5 w-1.5 rounded-full ${n > 0 ? (active ? "bg-white" : "bg-brand") : "bg-transparent"}`} />
                </button>
              );
            })}
          </div>
          <IconButton size="sm" label={t("cal.nextWeek")} icon={<ChevronRightIcon />} onClick={() => selectDay(addDays(day, 7))} />
        </div>
      )}

      {cab.isOwner && (masters.data?.length ?? 0) > 1 && (
        <div className="-mx-4 mb-4 flex gap-2 overflow-x-auto px-4 lg:mx-0 lg:flex-wrap lg:px-0">
          <Chip active={master === "all"} onClick={() => setMaster("all")}>
            {t("cal.allMasters")}
          </Chip>
          {masters.data?.map((m, i) => (
            <Chip key={m.id} active={master === m.id} onClick={() => setMaster(m.id)}>
              {wide && <span className={`mr-1.5 inline-block h-2 w-2 rounded-full align-middle ${masterColor(i)}`} aria-hidden="true" />}
              {m.name}
            </Chip>
          ))}
        </div>
      )}

      {!wide && <div className="mb-3 text-sm font-semibold text-ink">{fmtDay(day, lang, { weekday: "long", day: "numeric", month: "long" })}</div>}
      {agenda.isPending && (wide ? <Spinner /> : <SkeletonList rows={3} />)}
      {agenda.isError && <Notice kind="error">{t("common.error")}</Notice>}
      {!cab.isOwner && !cab.masterId && <Notice>{t("cal.noMasterLink")}</Notice>}
      {!wide && agenda.data && rows.length === 0 && <EmptyState icon={<CalendarIcon />} title={t("cal.emptyDay")} />}

      {wide && agenda.data && (
        <WeekGrid
          week={week}
          day={day}
          today={today}
          tz={tz}
          lang={lang}
          bookings={gridBookings}
          timeOff={gridTimeOff}
          masterCount={masterCount}
          countByDay={countByDay}
          onSelectDay={selectDay}
          onOpenBooking={openBooking}
        />
      )}

      {!wide && (
        <div className="space-y-3">
          {rows.map((r) =>
            r.kind === "off" ? (
              <div key={`off-${r.o.id}`} className="rounded-card border border-dashed border-line-strong bg-page px-4 py-3 text-sm text-ink-2">
                <span className="font-medium">
                  {r.o.period.start <= dayStart(day, tz) && r.o.period.end >= dayStart(addDays(day, 1), tz)
                    ? t("cal.allDay")
                    : `${fmtTime(r.o.period.start, tz, lang)}–${fmtTime(r.o.period.end, tz, lang)}`}
                </span>{" "}
                · {t(`cal.offKind.${r.o.kind}`)}
                {showMasterName && ` · ${masterName(r.o.masterId)}`}
              </div>
            ) : (
              <button
                key={r.b.id}
                type="button"
                onClick={() => openBooking(r.b.id)}
                className={`relative block w-full overflow-hidden rounded-card border bg-white px-4 py-3 text-left shadow-card transition hover:border-line-strong ${focusRing} ${
                  r.b.status === "pending" ? "border-amber-300" : "border-line"
                } ${isDead(r.b.status) ? "opacity-60" : ""}`}
              >
                {/* Полоса цвета мастера слева, как в сетке недели на широком экране. */}
                {showMasterName && <span className={`absolute inset-y-3 left-0 w-1 rounded-r ${masterColor(masterIndex(r.b.masterId))}`} aria-hidden="true" />}
                <div className="flex items-center justify-between gap-2">
                  <span className={`font-semibold tabular-nums text-ink ${isDead(r.b.status) ? "line-through" : ""}`}>
                    {fmtTime(r.b.period.start, tz, lang)}–{fmtTime(r.b.period.end, tz, lang)}
                  </span>
                  <Badge tone={statusTone[r.b.status]} dot>
                    {t(`cal.status.${r.b.status}`)}
                  </Badge>
                </div>
                <div className="mt-1 truncate font-medium text-ink">{clientLabel(r.b)}</div>
                <div className="truncate text-sm text-ink-2">{detailsLabel(r.b)}</div>
              </button>
            ),
          )}
        </div>
      )}

      {!wide && (
        <button
          type="button"
          onClick={() => openModal(setNewOpen)}
          disabled={!canCreate}
          className={`fixed right-4 bottom-[calc(5rem+env(safe-area-inset-bottom))] z-10 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand text-white shadow-pop transition hover:bg-brand-hover active:scale-[0.97] disabled:opacity-40 ${focusRing}`}
          aria-label={t("cal.new")}
        >
          <PlusIcon className="h-7 w-7" />
        </button>
      )}

      <NewBookingSheet
        open={newOpen}
        onClose={() => setNewOpen(false)}
        day={day}
        defaultMasterId={cab.isOwner ? (master !== "all" ? master : (masters.data?.[0]?.id ?? null)) : cab.masterId}
      />
      <TimeOffSheet
        open={offOpen}
        onClose={() => setOffOpen(false)}
        day={day}
        defaultMasterId={cab.isOwner ? (master !== "all" ? master : null) : cab.masterId}
      />
      <BookingSheet bookingId={openBookingId} onClose={() => openBooking(null)} />
    </div>
  );
}
